import { getSupabase } from "./supabase";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";

async function getToken(): Promise<string | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error((err as any).error ?? res.statusText), { status: res.status });
  }
  return res.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────────────

export type OrgInfo = { id: string; slug: string; display_name: string };

export type AccessGrant = {
  collection_id: string; collection_slug: string; collection_name: string;
  group_name: string | null; level: string;
  org_id: string; org_slug: string; org_display_name: string;
};

export type Collection = {
  id: string; slug: string; name: string; description: string | null;
  group_name: string | null; is_public: boolean; is_single_doc: boolean;
  display_order: number; doc_count?: number; public_url: string;
};

export type DocListItem = {
  id: string; slug: string; title: string;
  display_order: number; depth: number; parent_id: string | null; published_at: string;
};

export type DocFull = DocListItem & { content: string };

export type CollectionAccessGrant = {
  id: string; user_id: string; user_email: string; level: string; granted_at: string;
};

export type SearchResult = {
  id: string; title: string; snippet: string;
  org_slug: string; collection_slug: string; doc_slug: string; url: string; is_public: boolean;
};

export type ChatSource = { slug: string; title: string };
export type ChatResponse = { answer: string; sources: ChatSource[] };

// ── Organizations ─────────────────────────────────────────────────────────────

export const api = {
  me: {
    organizations: () => apiFetch<OrgInfo[]>("/api/me/organizations"),
    access: () => apiFetch<AccessGrant[]>("/api/me/access"),
  },

  orgs: {
    get: (org: string) => apiFetch<OrgInfo>(`/api/orgs/${org}`),
    collections: (org: string) => apiFetch<Collection[]>(`/api/orgs/${org}/collections`),
    collection: (org: string, collection: string) =>
      apiFetch<Collection>(`/api/orgs/${org}/collections/${collection}`),
    setVisibility: (org: string, collection: string, is_public: boolean) =>
      apiFetch<{ ok: boolean; is_public: boolean; public_url: string }>(
        `/api/orgs/${org}/collections/${collection}/visibility`,
        { method: "PATCH", body: JSON.stringify({ is_public }) },
      ),
  },

  docs: {
    list: (org: string, collection: string) =>
      apiFetch<{ single_doc: boolean; documents?: DocListItem[]; document?: DocFull }>(
        `/api/${org}/${collection}`,
      ),
    get: (org: string, collection: string, slug: string) =>
      apiFetch<{ document: DocFull; documents: DocListItem[] }>(
        `/api/${org}/${collection}/${slug}`,
      ),
    listPublic: (org: string, collection: string) =>
      apiFetch<{ single_doc: boolean; documents?: DocListItem[]; document?: DocFull }>(
        `/api/public/${org}/${collection}`,
      ),
    getPublic: (org: string, collection: string, slug: string) =>
      apiFetch<{ document: DocFull; documents: DocListItem[] }>(
        `/api/public/${org}/${collection}/${slug}`,
      ),
    reorder: (org: string, collection: string, order: string[]) =>
      apiFetch<{ ok: boolean }>(
        `/api/${org}/${collection}/documents/order`,
        { method: "PATCH", body: JSON.stringify({ order }) },
      ),
  },

  access: {
    list: (org: string, collection: string) =>
      apiFetch<CollectionAccessGrant[]>(`/api/${org}/${collection}/access`),
    grant: (org: string, collection: string, email: string, level = "VIEWER") =>
      apiFetch<CollectionAccessGrant>(
        `/api/${org}/${collection}/access`,
        { method: "POST", body: JSON.stringify({ email, level }) },
      ),
    revoke: (org: string, collection: string, id: string) =>
      apiFetch<{ ok: boolean }>(`/api/${org}/${collection}/access/${id}`, { method: "DELETE" }),
  },

  search: (q: string, org?: string, collection?: string) => {
    const params = new URLSearchParams({ q });
    if (org) params.set("org", org);
    if (collection) params.set("collection", collection);
    return apiFetch<{ results: SearchResult[] }>(`/api/search?${params}`);
  },

  chat: (org: string, collection: string, question: string) =>
    apiFetch<ChatResponse>(`/api/${org}/${collection}/chat`, {
      method: 'POST',
      body: JSON.stringify({ question }),
    }),
};
